/**
 * WorkshopCheckoutUpgradeCard — Phase 2 primary conversion surface.
 *
 * Rendered on the workshop event page when a Connect user is about to pay full
 * price. Presents the value comparison and offers a single-click upgrade that
 * (via /api/workshops/{id}/upgrade-and-register) starts a Thrive subscription
 * and auto-applies the just-granted monthly credit to this workshop.
 *
 * Design notes: on-brand cream card, teal/navy accents, subtle "Sparkles" motif,
 * animated entrance. NOT a modal or banner — sits inline above the standard
 * "Buy Ticket" button so the choice is presented, not interrupted.
 *
 * Failure handling: if the Stripe subscription flow errors or is cancelled,
 * the workshop registration is untouched. The user can still complete the
 * standard $45 checkout via the "Just buy this workshop" fallback.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Check, Loader2, ArrowRight, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const MONTHLY_PRICE = 39;
const ANNUAL_PRICE = 390;
const MEMBER_DISCOUNT_PCT = 30;

export default function WorkshopCheckoutUpgradeCard({
  event,
  basePriceCents,
  onPayFullPrice,
  fullPriceLoading = false,
}) {
  const [cycle, setCycle] = useState('monthly'); // 'monthly' | 'annual'
  const [upgrading, setUpgrading] = useState(false);

  const basePrice = basePriceCents / 100;
  const monthlyCost = cycle === 'annual' ? ANNUAL_PRICE / 12 : MONTHLY_PRICE;
  // Annual savings if she attends 1 workshop/month over 12 months
  const annualSavings = Math.max(0, basePrice * 12 - ANNUAL_PRICE);
  const monthlyAnnualSavings = Math.max(0, basePrice * 12 - MONTHLY_PRICE * 12);
  const displaySavings = cycle === 'annual' ? annualSavings : monthlyAnnualSavings;

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const res = await api.post(`/workshops/${event.id}/upgrade-and-register`, {
        billing_cycle: cycle,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('Missing checkout URL');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Upgrade could not be started right now.';
      toast.error(msg, {
        description: "You can still purchase this workshop at full price — nothing has been charged.",
        duration: 6000,
      });
      setUpgrading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="relative overflow-hidden rounded-3xl border-2 border-teal/20 bg-cream shadow-card mb-6"
        data-testid="workshop-upgrade-card"
      >
        {/* Decorative teal gradient bar */}
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal via-teal-deep to-navy" />

        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-teal/15 flex items-center justify-center shrink-0">
              <Sparkles className="text-teal-deep" size={20} />
            </div>
            <div>
              <p className="text-[11px] tracking-[0.2em] uppercase text-teal-deep font-semibold mb-1">
                Thrive members attend free
              </p>
              <h3 className="font-display text-2xl md:text-3xl text-navy leading-tight">
                Pay <span className="line-through opacity-40">${basePrice.toFixed(0)}</span> — or join Thrive and this workshop is on us.
              </h3>
            </div>
          </div>

          {/* Value comparison strip */}
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            <ValueTile
              label="This workshop"
              value={`$${basePrice.toFixed(2)}`}
              sub="one-time"
              muted
            />
            <ValueTile
              label={cycle === 'annual' ? 'Thrive · Annual' : 'Thrive · Monthly'}
              value={`$${cycle === 'annual' ? ANNUAL_PRICE : MONTHLY_PRICE}`}
              sub={cycle === 'annual' ? '/year (≈ $32.50/mo)' : '/month'}
              highlight
            />
            <ValueTile
              label="Save over 12 mo"
              value={`$${displaySavings.toFixed(0)}`}
              sub="attending monthly"
              accent
            />
          </div>

          {/* Benefits */}
          <ul className="space-y-2 mb-6" data-testid="upgrade-benefits">
            <Benefit>1 workshop free every calendar month — resets on the 1st</Benefit>
            <Benefit>Additional workshops at {MEMBER_DISCOUNT_PCT}% off (${(basePrice * (100 - MEMBER_DISCOUNT_PCT) / 100).toFixed(2)} today)</Benefit>
            <Benefit>24-hour early registration for every workshop (48h if you join before Sep 30)</Benefit>
            <Benefit>All member gatherings + community board access</Benefit>
          </ul>

          {/* Billing cycle toggle */}
          <div className="mb-5">
            <div
              className="inline-flex p-1 rounded-full bg-white border border-border-subtle"
              role="tablist"
              aria-label="Billing cycle"
              data-testid="upgrade-cycle-toggle"
            >
              <button
                onClick={() => setCycle('monthly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  cycle === 'monthly' ? 'bg-navy text-white shadow-sm' : 'text-text-mid hover:text-navy'
                }`}
                data-testid="upgrade-cycle-monthly"
              >
                Monthly · $39
              </button>
              <button
                onClick={() => setCycle('annual')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  cycle === 'annual' ? 'bg-navy text-white shadow-sm' : 'text-text-mid hover:text-navy'
                }`}
                data-testid="upgrade-cycle-annual"
              >
                Annual · $390 <span className={`ml-1 text-xs ${cycle === 'annual' ? 'text-seafoam' : 'text-teal-deep'}`}>save 2 mo</span>
              </button>
            </div>
          </div>

          {/* Primary CTA */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleUpgrade}
              disabled={upgrading || fullPriceLoading}
              className="flex-1 bg-teal hover:bg-teal-deep text-white rounded-full px-6 py-6 text-base font-medium shadow-md hover:shadow-lg transition-all"
              data-testid="upgrade-and-register-btn"
            >
              {upgrading ? (
                <><Loader2 size={18} className="animate-spin mr-2" /> Redirecting…</>
              ) : (
                <>
                  <Sparkles size={18} className="mr-2" />
                  Upgrade & attend free
                  <ArrowRight size={16} className="ml-2" />
                </>
              )}
            </Button>
            <Button
              onClick={onPayFullPrice}
              disabled={fullPriceLoading || upgrading}
              variant="outline"
              className="rounded-full px-6 py-6 text-base border-2 border-navy/15 text-navy hover:bg-navy/5"
              data-testid="upgrade-decline-fullprice-btn"
            >
              {fullPriceLoading ? (
                <Loader2 size={18} className="animate-spin mr-2" />
              ) : null}
              Just this workshop — ${basePrice.toFixed(0)}
            </Button>
          </div>

          {/* Trust line */}
          <div className="mt-5 flex items-start gap-2 text-xs text-text-mid">
            <Info size={14} className="shrink-0 mt-0.5" />
            <p>
              Cancel Thrive anytime. If checkout fails, your workshop registration stays available and you can pay the ${basePrice.toFixed(0)} directly — nothing is charged twice.
            </p>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function ValueTile({ label, value, sub, muted = false, highlight = false, accent = false }) {
  const tone = highlight
    ? 'bg-navy text-white border-navy'
    : accent
    ? 'bg-teal/10 text-teal-deep border-teal/30'
    : 'bg-white/70 text-navy border-border-subtle';
  const labelTone = highlight ? 'text-white/70' : accent ? 'text-teal-deep' : 'text-text-mid';
  const subTone = highlight ? 'text-white/60' : accent ? 'text-teal-deep/80' : 'text-text-mid';
  return (
    <div className={`rounded-2xl border ${tone} px-4 py-3 ${muted ? 'opacity-90' : ''}`}>
      <p className={`text-[10px] uppercase tracking-wider ${labelTone} mb-1`}>{label}</p>
      <p className="font-display text-2xl leading-none">{value}</p>
      <p className={`text-xs mt-1 ${subTone}`}>{sub}</p>
    </div>
  );
}

function Benefit({ children }) {
  return (
    <li className="flex items-start gap-2 text-sm text-navy">
      <div className="w-5 h-5 rounded-full bg-teal/15 flex items-center justify-center shrink-0 mt-0.5">
        <Check size={12} className="text-teal-deep" strokeWidth={3} />
      </div>
      <span>{children}</span>
    </li>
  );
}
